'use client';

import {
  type ComponentProps,
  type FormEvent,
  useEffect,
  useState,
} from 'react';
import { useParams, useRouter } from 'next/navigation';
import { Search } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Button, Input, Switch } from 'rizzui';

function useTrackingAction() {
  const params = useParams<{ locale?: string }>();
  const locale = typeof params?.locale === 'string' ? params.locale : 'en';

  return `/${locale}/track-shipment`;
}

export function LandingSourceButton(props: ComponentProps<typeof Button>) {
  return <Button {...props} />;
}

export function LandingHeaderSearchForm() {
  const trackingAction = useTrackingAction();

  return (
    <form action={trackingAction} method="get">
      <div className="at-search-form-input">
        <Input
          type="search"
          name="load"
          aria-label="Find a shipment"
          placeholder="Find what you need…"
          className="landing-header-search-shell"
          inputClassName="landing-header-search-input"
          required
        />
        <span className="at-search-focus-border" />
        <Button
          className="at-search-form-btn at-btn"
          type="submit"
          variant="text"
        >
          <span>
            <span className="text-1">Search</span>
            <span className="text-2">Search</span>
          </span>
          <Search aria-hidden="true" width={18} height={18} />
        </Button>
      </div>
    </form>
  );
}

/**
 * The source design's icon toggle, not a RizzUI `Switch`, the documented exception to the
 * root AGENTS.md rule. The template CSS keys entirely off this structure
 * (`#switch:checked + .icon--moon`, `#switch:checked ~ .icon--sun`), which only a native
 * checkbox with those two sibling spans can produce. A RizzUI Switch renders a
 * track-and-knob pill instead, and its styling lives in home-overrides.css, which the
 * sub-pages do not load, so it showed up unstyled everywhere except the home page.
 */
export function LandingThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const dark = mounted && resolvedTheme === 'dark';

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="dark-light-mode">
      <label className="toggle" htmlFor="switch">
        <input
          type="checkbox"
          className="input"
          id="switch"
          aria-label={`Switch to ${dark ? 'light' : 'dark'} mode`}
          checked={dark}
          onChange={(event) =>
            setTheme(event.target.checked ? 'dark' : 'light')
          }
        />
        <span className="icon icon--moon">
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            {' '}
            <path
              d="M7.51166 0C6.33857 1.09609 5.6053 2.65694 5.6053 4.38904C5.6053 7.70586 8.29416 10.3947 11.611 10.3947C13.3431 10.3947 14.9039 9.66146 16 8.48836C15.7439 12.6798 12.2635 16 8.00757 16C3.58511 16 0 12.4149 0 7.99245C0 3.73655 3.32017 0.256105 7.51166 0Z"
              fill="currentColor"
            />{' '}
          </svg>
        </span>
        <span className="icon icon--sun">
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            width="28"
            height="28"
          >
            {' '}
            <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z">
              {' '}
            </path>{' '}
          </svg>
        </span>
      </label>
    </div>
  );
}

export function LandingNewsletterForm({
  action = 'Get started',
}: {
  /** Submit label, the source design changes it per page. */
  action?: string;
}) {
  const router = useRouter();
  const params = useParams<{ locale?: string }>();
  const locale = typeof params?.locale === 'string' ? params.locale : 'en';
  const [email, setEmail] = useState('');

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const query = new URLSearchParams({ email: email.trim() });
    router.push(`/${locale}/get-quote?${query.toString()}`);
  }

  return (
    <form
      className="footer-12__cta-form at_fade_anim"
      data-fade-from="bottom"
      data-delay=".25"
      onSubmit={handleSubmit}
    >
      <Input
        type="email"
        name="email"
        aria-label="Business email"
        placeholder="Enter your business email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        className="footer-12__cta-input-shell"
        inputClassName="footer-12__cta-input"
        maxLength={254}
        required
      />
      <Button className="footer-12__cta-submit" type="submit" variant="text">
        <span>{action}</span>
      </Button>
    </form>
  );
}
